// src/context/modules/eventsModule.js
import { useState, useCallback } from "react";
import {
  fetchEvents,
  updateEvent,
  createEvent,
  deleteEvent,
} from "../../api/eventsApi";

export const useEventsModule = () => {
  // State
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Operations
  const refreshEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const newEvents = await fetchEvents();
      setEvents(newEvents);
      return newEvents;
    } catch (error) {
      console.error("Error refreshing events:", error);
      throw error;
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const addEvent = useCallback(
    async (eventData) => {
      setEventsLoading(true);
      try {
        const newEvent = await createEvent(eventData);
        setEvents((prev) => [...prev, newEvent]);
        return newEvent;
      } catch (error) {
        console.error("Error adding event:", error);
        throw error;
      } finally {
        await refreshEvents();
        setEventsLoading(false);
      }
    },
    [refreshEvents]
  );

  const updateEventData = useCallback(
    async (id, eventData) => {
      setEventsLoading(true);
      try {
        const updatedEvent = await updateEvent(id, eventData);
        setEvents((prev) =>
          prev.map((event) => (event.id === id ? updatedEvent : event))
        );
        return updatedEvent;
      } catch (error) {
        console.error("Error updating event:", error);
        throw error;
      } finally {
        await refreshEvents();
        setEventsLoading(false);
      }
    },
    [refreshEvents]
  );

  const removeEvent = useCallback(
    async (id) => {
      setEventsLoading(true);
      try {
        await deleteEvent(id);
        setEvents((prev) => prev.filter((event) => event.id !== id));
      } catch (error) {
        console.error("Error deleting event:", error);
        throw error;
      } finally {
        await refreshEvents();
        setEventsLoading(false);
      }
    },
    [refreshEvents]
  );

  // Initial data load
  const loadInitialEvents = async () => {
    try {
      setEventsLoading(true);
      const eventsData = await fetchEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error("Error loading events:", error);
    } finally {
      setEventsLoading(false);
    }
  };

  return {
    // State
    events,
    eventsLoading,

    // Operations
    refreshEvents,
    addEvent,
    updateEvent: updateEventData,
    removeEvent,
    loadInitialEvents,
  };
};
